import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Row,
  Col,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Descriptions,
  Modal,
  Pagination
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  DollarOutlined
} from '@ant-design/icons';
// 移除路由相关导入
import { getSales, deleteSale } from '../services/api';
import SaleForm from './SaleForm';
import moment from 'moment';
import useResponsive from '../hooks/useResponsive';

const { Search } = Input;
const { Option } = Select;

const SaleList = ({ onNavigate, onDataChanged }) => {
  const { isMobile } = useResponsive();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailSale, setDetailSale] = useState(null);

  useEffect(() => {
    fetchSales();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      console.log('获取销售记录参数:', params);
      const response = await getSales(params);
      console.log('销售记录API响应:', response);
      const normalized = (response.sales || []).map(item => ({
        ...item,
        id: item.id ?? item._id ?? item.sale_id ?? item.saleId,
        salePrice: item.salePrice ?? item.sale_price,
        paymentMethod: item.paymentMethod ?? item.payment_method,
        buyerName: item.buyerName ?? item.buyer_name,
        buyerContact: item.buyerContact ?? item.buyer_contact,
        saleDate: item.saleDate ?? item.sale_date ?? item.created_at,
        // 后端已返回 account 详情
        account: item.account || item.accountId || null
      }));
      setSales(normalized);
      setPagination(prev => ({
        ...prev,
        total: response.total
      }));
    } catch (error) {
      message.error('获取销售记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 粉丝区间筛选改到账号管理，不在销售管理里展示

  const handleDelete = async (id) => {
    if (!id) {
      console.warn('删除失败：缺少销售记录ID');
      message.error('无法删除：记录ID缺失');
      return;
    }
    try {
      await deleteSale(id);
      message.success('删除成功');
      fetchSales();
      onDataChanged && onDataChanged();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleNavigate = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchSales();
    onDataChanged && onDataChanged();
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'orange', text: '待处理' },
      completed: { color: 'green', text: '已完成' },
      cancelled: { color: 'red', text: '已取消' },
      refunded: { color: 'purple', text: '已退款' }
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getPaymentMethodTag = (method) => {
    const methodConfig = {
      alipay: { color: 'blue', text: '支付宝' },
      wechat: { color: 'green', text: '微信' },
      bank: { color: 'purple', text: '银行转账' },
      cash: { color: 'orange', text: '现金' },
      other: { color: 'default', text: '其他' }
    };
    const config = methodConfig[method] || { color: 'default', text: method };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '账号信息',
      key: 'account',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.account?.username || '未知账号'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            粉丝: {record.account?.followers?.toLocaleString?.() || '0'}
          </div>
          {record.account?.country ? (
            <div style={{ fontSize: '12px', color: '#666' }}>地区: {record.account.country}</div>
          ) : null}
        </div>
      )
    },
    {
      title: '买家信息',
      key: 'buyer',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.buyerName}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.buyerContact}
          </div>
        </div>
      )
    },
    {
      title: '销售价格',
      dataIndex: 'salePrice',
      key: 'salePrice',
      render: (value) => (
        <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
          ¥{value}
        </span>
      ),
      sorter: (a, b) => a.salePrice - b.salePrice
    },
    {
      title: '利润',
      dataIndex: 'profit',
      key: 'profit',
      render: (value) => (
        <span style={{ color: value > 0 ? '#52c41a' : '#f5222d' }}>
          ¥{value}
        </span>
      ),
      sorter: (a, b) => a.profit - b.profit
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method) => getPaymentMethodTag(method)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status)
    },
    {
      title: '销售日期',
      dataIndex: 'saleDate',
      key: 'saleDate',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => new Date(a.saleDate) - new Date(b.saleDate)
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => { setDetailSale(record); setDetailVisible(true); }}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => { setDetailSale(record); setModalVisible(true); }}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这条销售记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: '16px' }}>
          {isMobile ? (
            <div>
              <div style={{ marginBottom: 12 }}>
                <Search
                  placeholder="搜索买家姓名或联系方式"
                  style={{ width: '100%' }}
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                />
              </div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={12}>
                  <Select
                    placeholder="状态筛选"
                    style={{ width: '100%' }}
                    allowClear
                    onChange={handleStatusFilter}
                  >
                    <Option value="pending">待处理</Option>
                    <Option value="completed">已完成</Option>
                    <Option value="cancelled">已取消</Option>
                    <Option value="refunded">已退款</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Button block size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setDetailSale(null); setModalVisible(true); }}>添加</Button>
                    </Col>
                    <Col span={12}>
                      <Button block size="small" icon={<ReloadOutlined />} onClick={fetchSales}>刷新</Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          ) : (
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space wrap>
                  <Search
                    placeholder="搜索买家姓名或联系方式"
                    style={{ width: 300 }}
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                  />
                  <Select
                    placeholder="状态筛选"
                    style={{ width: 120 }}
                    allowClear
                    onChange={handleStatusFilter}
                  >
                    <Option value="pending">待处理</Option>
                    <Option value="completed">已完成</Option>
                    <Option value="cancelled">已取消</Option>
                    <Option value="refunded">已退款</Option>
                  </Select>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setDetailSale(null); setModalVisible(true); }}
                  >
                    添加销售
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchSales}
                  >
                    刷新
                  </Button>
                </Space>
              </Col>
            </Row>
          )}
        </div>

        {isMobile ? (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {sales.map(record => (
                <Card key={record.id} size="small">
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {record.account?.username || '未知账号'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      粉丝: {record.account?.followers?.toLocaleString?.() || '0'}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold' }}>{record.buyerName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {record.buyerContact}
                    </div>
                  </div>
                  
                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col span={12}>
                      <div style={{ fontSize: '12px', color: '#666' }}>销售价格</div>
                      <div style={{ color: '#f5222d', fontWeight: 'bold' }}>
                        ¥{record.salePrice}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: '12px', color: '#666' }}>利润</div>
                      <div style={{ color: record.profit > 0 ? '#52c41a' : '#f5222d' }}>
                        ¥{record.profit}
                      </div>
                    </Col>
                  </Row>
                  
                  <Row gutter={8} style={{ marginBottom: 12 }}>
                    <Col span={12}>
                      <div style={{ fontSize: '12px', color: '#666' }}>支付方式</div>
                      {getPaymentMethodTag(record.paymentMethod)}
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: '12px', color: '#666' }}>状态</div>
                      {getStatusTag(record.status)}
                    </Col>
                  </Row>
                  
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: 12 }}>
                    {moment(record.saleDate).format('YYYY-MM-DD HH:mm')}
                  </div>
                  
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => { setDetailSale(record); setDetailVisible(true); }}>详情</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setDetailSale(record); setModalVisible(true); }}>编辑</Button>
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                      <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                  </div>
                </Card>
              ))}
            </Space>
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                onChange={(page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || prev.pageSize
                  }));
                }}
              />
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={sales}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || prev.pageSize
                }));
              }
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      <Modal
        title={detailSale ? '编辑销售' : '添加销售'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={isMobile ? '95%' : 800}
      >
        <SaleForm
          accountId={detailSale?.account_id || detailSale?.accountId?.id}
          saleId={detailSale?.id}
          sale={detailSale}
          mode={detailSale ? 'edit' : 'create'}
          onSuccess={handleModalSuccess}
          onCancel={handleModalClose}
        />
      </Modal>

      <Modal
        title="销售详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 700}
      >
        {detailSale && (
          <Descriptions bordered size={isMobile ? 'small' : 'default'} column={isMobile ? 1 : 2}>
            <Descriptions.Item label="账号">{detailSale.account?.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="粉丝数">{detailSale.account?.followers?.toLocaleString?.() || 0}</Descriptions.Item>
            {detailSale.account?.password && (
              <Descriptions.Item label="密码">{detailSale.account.password}</Descriptions.Item>
            )}
            {detailSale.account?.email && (
              <Descriptions.Item label="邮箱">{detailSale.account.email}</Descriptions.Item>
            )}
            {detailSale.account?.phone && (
              <Descriptions.Item label="手机号">{detailSale.account.phone}</Descriptions.Item>
            )}
            {detailSale.account?.country && (
              <Descriptions.Item label="地区">{detailSale.account.country}</Descriptions.Item>
            )}
            <Descriptions.Item label="分类">{detailSale.account?.category || '未分类'}</Descriptions.Item>
            {detailSale.account?.api_key && (
              <Descriptions.Item label="API">
                {/^https?:\/\//i.test(String(detailSale.account.api_key).trim()) ? (
                  <a
                    href={String(detailSale.account.api_key).trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {detailSale.account.api_key}
                  </a>
                ) : (
                  detailSale.account.api_key
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="买家">
              {detailSale.buyerName}
            </Descriptions.Item>
            <Descriptions.Item label="联系方式">
              {detailSale.buyerContact}
            </Descriptions.Item>
            <Descriptions.Item label="价格">¥{detailSale.salePrice}</Descriptions.Item>
            <Descriptions.Item label="利润">¥{detailSale.profit}</Descriptions.Item>
            <Descriptions.Item label="支付方式">{getPaymentMethodTag(detailSale.paymentMethod)}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(detailSale.status)}</Descriptions.Item>
            <Descriptions.Item label="备注">{detailSale.notes || '-'}</Descriptions.Item>
            {detailSale.account?.window_status && (
              <Descriptions.Item label="橱窗状态">
                {detailSale.account.window_status === 'open' ? '已开' : '未开'}
                {detailSale.account.window_status === 'open' && detailSale.account.window_open_date ? `（${detailSale.account.window_open_date}）` : ''}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default SaleList;
